import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { addProfileData } from "../../../../redux/slices/profileslice";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { GetCategoryList } from "../../services/category.service";


import { editBusinessProfile, profileDetails } from "../../services/user.service";
import { SWAL_SETTINGS, ROLE, INDIAN_STATES } from "../../../../utils/Constants";
import { handleServerValidations, getUser, setLocalKey } from "../../../../utils/commonfunction";

import { Upload, Trash2, Plus } from "lucide-react";

function BusinessProfile() {
  const { t } = useTranslation();
  const isAdvertiserContext = window.location.href.includes("/advertiser");
  const fallbackRole = ROLE.ADVERTISER
  console.log("dd11222", process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  let userData = getUser(ROLE.USER);
  if (!userData || Object.keys(userData).length === 0) {
    userData = getUser(ROLE.ADVERTISER) || {};
  }
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [previewImage, setPreviewImage] = useState(userData?.store_image || "/images/business-profile.png");
  const [storeImagesList, setStoreImagesList] = useState(
    userData?.store_images && Array.isArray(userData.store_images)
      ? userData.store_images.map(img => ({ type: 'existing', url: img }))
      : []
  );
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  console.log("profileData", profileData);
  const [coords, setCoords] = useState({
    latitude: userData?.store_location?.latitude || userData?.latitude || "",
    longitude: userData?.store_location?.longitude || userData?.longitude || "",
  });
  const [categories, setCategories] = useState([]);

  // Fetch the latest profile data from the backend
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileDetails();
        if (res?.data) {
          setProfileData(res.data);
          setPreviewImage(res.data.store_image || "/images/business-profile.png");
          if (res.data.store_images && Array.isArray(res.data.store_images)) {
            setStoreImagesList(res.data.store_images.map(img => ({ type: 'existing', url: img })));
          }
          // Update Redux & Local storage so other parts of the app are aware
          const existingUser = getUser(ROLE.USER) || {};
          const mergedData = { ...existingUser, ...res.data };
          dispatch(addProfileData(mergedData));
          setLocalKey(ROLE.USER, JSON.stringify(mergedData));
          setLocalKey(ROLE.ADVERTISER, JSON.stringify(mergedData));
        }
      } catch (err) {
        console.error("Failed to fetch fresh business profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();

    const fetchCategories = async () => {
      try {
        const res = await GetCategoryList({ per_page: 100, page: 1 });
        if (res?.list) {
          const options = res.list.map(cat => ({
            value: cat._id,
            label: cat.title
          }));
          setCategories(options);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, [dispatch, fallbackRole]);

  const validationSchema = Yup.object().shape({
    business_handle: Yup.string().trim().required(t("vaildation.business_handle", "Business Profile Name is required")),
    store_name: Yup.string().required("Store name is required"),
    store_address: Yup.string().required("Store address is required"),
    state: Yup.string().nullable(),
    gst_number: Yup.string()
      .trim()
      .nullable()
      .test(
        "gst-length",
        t("vaildation.gst_must_number", "GST number must be 15 characters"),
        (value) => !value || value.length === 15
      )
      .test(
        "valid-gst",
        t("vaildation.gst_invalid", "Invalid GST number format"),
        (value) =>
          !value ||
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.toUpperCase())
      ),
    categoryIds: Yup.array()
      .min(1, "Please select at least one category")
      .required("Categories are required"),
  });

  const initialValues = {
    business_handle: profileData?.business_handle || userData?.business_handle || "",
    store_name: profileData?.store_name || userData?.store_name || "",
    store_address: profileData?.store_location?.store_address || userData?.store_location?.store_address || "",
    state: profileData?.state || profileData?.store_location?.state || userData?.state || userData?.store_location?.state || "",
    gst_number: profileData?.gst_number || userData?.gst_number || "",
    shipping_policy: profileData?.shipping_policy || userData?.shipping_policy || "",
    categoryIds: profileData?.categoryIds || userData?.categoryIds || [],
    image: profileData?.store_image || userData?.store_image,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      const rawHandle = values.business_handle ? values.business_handle.trim() : "";
      const cleanedHandle = rawHandle
        .replace(/^https?:\/\/[^\/]+\/businessprofilename\//i, "")
        .replace(/^businessprofilename\//i, "")
        .replace(/^@+/, "");
      formData.append("business_handle", cleanedHandle);
      formData.append("store_name", values.store_name);
      formData.append("store_address", values.store_address);
      formData.append("state", values.state || "");
      formData.append("gst_number", values.gst_number || "");
      formData.append("shipping_policy", values.shipping_policy || "");
      formData.append("latitude", values.latitude || coords.latitude || "");
      formData.append("longitude", values.longitude || coords.longitude || "");
      formData.append("categoryIds", JSON.stringify(values.categoryIds));

      if (values.image) formData.append("store_image", values.image);

      // Append retained existing store images & new uploaded store images
      const existingUrls = storeImagesList.filter(item => item.type === 'existing').map(item => item.url);
      formData.append("existing_store_images", JSON.stringify(existingUrls));

      storeImagesList.filter(item => item.type === 'new').forEach(item => {
        formData.append("store_images", item.file);
      });

      const response = await editBusinessProfile(formData);

      if (response.success) {

        const res = await profileDetails();

        if (res && res.data) {
          const existingUser = getUser(ROLE.USER) || {};
          const mergedData = { ...existingUser, ...res.data };
          dispatch(addProfileData(mergedData));
          setLocalKey(ROLE.USER, JSON.stringify(mergedData));
          setLocalKey(ROLE.ADVERTISER, JSON.stringify(mergedData));
          if (res.data.store_images && Array.isArray(res.data.store_images)) {
            setStoreImagesList(res.data.store_images.map(img => ({ type: 'existing', url: img })));
          }
        }

        const isKycDone = res?.data?.isKycRegistered || userData?.isKycRegistered;

        Swal.fire({
          icon: "success",
          text: response.message || "Store details updated successfully",
          ...SWAL_SETTINGS,
        });

        if (!isKycDone) {
          navigate("/advertiser/kyc-details");
        }

      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response),
          ...SWAL_SETTINGS,
        });
      }

    } catch (error) {
      handleServerValidations(error);
    } finally {
      setSubmitting(false);
    }
  };


  const displayData = profileData || userData;

  return (
    <>
      <div className="max-w-[100%] mx-auto">
        {displayData?.failed_verification_reason && !displayData?.is_kyc_verified && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r">
            <div className="flex items-start">
              <div className="ml-1">
                <h3 className="text-sm font-bold text-red-800">
                  Advertiser Verification Rejected
                </h3>
                <div className="mt-1 text-sm text-red-700">
                  <p><strong>Reason:</strong> {displayData.failed_verification_reason}</p>
                </div>
                <p className="mt-1 text-xs text-red-600">You can update your business and KYC details below to re-submit for review.</p>
              </div>
            </div>
          </div>
        )}
        <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ setFieldValue, setFieldTouched, values, isSubmitting }) => (
            <Form>
              <div className="user-profile-bg bg-primary-50 p-[15px] rounded-[10px] flex items-center justify-between gap-[15px] flex-wrap">
                <div className="user-image-title flex items-center gap-[10px]">
                  <img src={previewImage} className="w-[89px] h-[89px] object-cover border rounded-full border-2 !border-red-100" />
                  <h2 className="headings-h2 text-zinc-950">{values.store_name || t('advertiser.profile.business_profile')}</h2>
                </div>

                <label className="btn btn-pri cursor-pointer">
                  {t('advertiser.profile.upload_photo')}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPreviewImage(URL.createObjectURL(file));
                        setFieldValue("image", file);
                      }
                    }}
                  />
                </label>
              </div>

              <div className="edit-profile-form-bg mt-3">
                <div className="form-group">
                  <label>{t('advertiser.profile.business_handle', 'Business Profile Name')}</label>
                  <div className="flex items-center form-control-custom !p-0 overflow-hidden focus-within:!border-[var(--secondary-500,#FF63B5)]">
                    <span className="bg-zinc-100 text-zinc-600 text-xs sm:text-sm px-3.5 h-full flex items-center border-r border-zinc-200 select-text cursor-text whitespace-nowrap font-normal" style={{ userSelect: "text" }}>
                      {window.location.origin}/businessprofilename/
                    </span>
                    <Field
                      type="text"
                      name="business_handle"
                      placeholder={t('placeholder.business_handle', 'Enter business profile name')}
                      className="w-full h-full bg-transparent px-3 text-zinc-900 outline-none border-none shadow-none text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-0"
                    />
                  </div>
                  <ErrorMessage name="business_handle" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div className="form-group">
                  <label>{t('advertiser.profile.store_name')}</label>
                  <Field type="text" name="store_name" placeholder={t('advertiser.profile.store_name')} className="form-control-custom" />
                  <ErrorMessage name="store_name" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div className="form-group mb-3">
                  <label>{t('advertiser.profile.store_address')}</label>
                  {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                    <GooglePlacesAutocomplete
                      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                      selectProps={{
                        value: values.store_address ? { label: values.store_address, value: values.store_address } : null,
                        onChange: (val) => {
                          setFieldValue("store_address", val.label);
                          if (window.google) {
                            const geocoder = new window.google.maps.Geocoder();
                            const query = val?.value?.place_id
                              ? { placeId: val.value.place_id }
                              : { address: val.label };

                            geocoder.geocode(query, (results, status) => {
                              if (status === "OK" && results && results[0]) {
                                const lat = results[0].geometry.location.lat();
                                const lng = results[0].geometry.location.lng();
                                setCoords({ latitude: lat, longitude: lng });
                                setFieldValue("latitude", lat);
                                setFieldValue("longitude", lng);

                                const stateComp = results[0].address_components?.find((comp) =>
                                  comp.types.includes("administrative_area_level_1")
                                );
                                if (stateComp) {
                                  const foundState = INDIAN_STATES.find(
                                    (s) =>
                                      s.toLowerCase() === stateComp.long_name.toLowerCase() ||
                                      s.toLowerCase() === stateComp.short_name.toLowerCase()
                                  );
                                  if (foundState) {
                                    setFieldValue("state", foundState);
                                  }
                                }
                              }
                            });
                          }
                        },
                        placeholder: t('advertiser.profile.store_address'),
                        classNamePrefix: "react-select",
                        styles: {
                          control: (provided) => ({
                            ...provided,
                            backgroundColor: "#f9fafb",
                            borderColor: "#e5e7eb",
                            borderRadius: "8px",
                            padding: "2px",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "#d1d5db",
                            },
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                          }),
                        },
                      }}
                    />
                  ) : (
                    <Field type="text" name="store_address" placeholder={t('advertiser.profile.store_address')} className="form-control-custom" />
                  )}
                  <ErrorMessage name="store_address" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div className="form-group mb-3">
                  <label>{t('advertiser.profile.state', 'State')}</label>
                  <Select
                    name="state"
                    options={INDIAN_STATES.map((st) => ({ value: st, label: st }))}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder={t('placeholder.select_state', 'Select State')}
                    value={values.state ? { label: values.state, value: values.state } : null}
                    onChange={(selectedOption) => {
                      setFieldValue("state", selectedOption ? selectedOption.value : "");
                    }}
                    onBlur={() => setFieldTouched("state", true)}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: "#f9fafb",
                        borderColor: "#e5e7eb",
                        borderRadius: "8px",
                        padding: "2px",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#d1d5db",
                        },
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    }}
                  />
                  <ErrorMessage name="state" component="div" className="text-red-500 text-sm mt-1" />
                </div>


                <div className="form-group">
                  <label>{t('advertiser.profile.gst_number')}</label>
                  <input
                    type="text"
                    name="gst_number"
                    placeholder={t('advertiser.profile.gst_number')}
                    className="form-control-custom"
                    value={values.gst_number}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                      setFieldValue("gst_number", val);
                    }}
                    maxLength={15}
                    style={{ textTransform: "uppercase" }}
                  />
                  <ErrorMessage name="gst_number" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div className="form-group">
                  <label>{t('advertiser.profile.product_categories')}</label>
                  <Select
                    isMulti
                    name="categoryIds"
                    options={categories}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder={t('advertiser.profile.product_categories')}
                    value={categories.filter(option => values.categoryIds.includes(option.value))}
                    onChange={(selectedOptions) => {
                      const valuesSelected = selectedOptions ? selectedOptions.map(option => option.value) : [];
                      setFieldValue("categoryIds", valuesSelected);
                    }}
                    onBlur={() => setFieldValue("categoryIds", values.categoryIds, true)}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: "#f9fafb",
                        borderColor: "#e5e7eb",
                        borderRadius: "8px",
                        padding: "2px",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#d1d5db",
                        },
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    }}
                  />
                  <ErrorMessage name="categoryIds" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div className="form-group">
                  <label>{t('advertiser.profile.shipping_policy')}</label>
                  <Field
                    as="textarea"
                    name="shipping_policy"
                    placeholder={t('advertiser.profile.shipping_policy')}
                    className="form-control-custom"
                    rows={4}
                    style={{ resize: "vertical" }}
                  />
                  <ErrorMessage name="shipping_policy" component="div" className="text-red-500 text-sm mt-1" />
                </div>


                <div className="form-group mb-6">
                  <label className="block text-sm font-semibold text-zinc-800 mb-1">
                    Store Banner Images (Multiple)
                  </label>
                  <p className="text-xs text-zinc-500 mb-3">
                    Upload high-resolution photos of your store to display as banners on your store profile.
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-2">
                    {storeImagesList.map((item, idx) => (
                      <div key={idx} className="relative group aspect-video bg-zinc-100 rounded-lg overflow-hidden border border-zinc-200 shadow-sm">
                        <img
                          src={item.type === 'existing' ? item.url : item.preview}
                          alt={`Store Banner ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setStoreImagesList(prev => prev.filter((_, i) => i !== idx));
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-all opacity-90 hover:opacity-100 cursor-pointer"
                          title="Remove image"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-zinc-300 hover:border-[#FF63B5] rounded-lg cursor-pointer bg-zinc-50 hover:bg-pink-50/40 transition-all text-center p-3">
                      <Upload className="w-6 h-6 text-zinc-400 group-hover:text-[#FF63B5] mb-1" />
                      <span className="text-xs font-semibold text-zinc-700">Upload Banners</span>
                      <span className="text-[10px] text-zinc-400">Multiple files supported</span>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          if (files.length > 0) {
                            const newItems = files.map(file => ({
                              type: 'new',
                              file,
                              preview: URL.createObjectURL(file)
                            }));
                            setStoreImagesList(prev => [...prev, ...newItems]);
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>

                {/* 🟢 Hidden latitude & longitude but still submitted */}
                <input type="hidden" name="latitude" value={coords.latitude} />
                <input type="hidden" name="longitude" value={coords.longitude} />

                <button type="submit" className="btn btn-pri min-w-[181px]" disabled={isSubmitting}>
                  {isSubmitting ? t('advertiser.profile.updating') : t('advertiser.profile.update')}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
}

export default BusinessProfile;
